import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IGenericLabelValueFieldsConfigFormModel,
  IFormbuilderFieldTypeConfiguration,
} from '../../../models/form-builder-types-configurations.model';
import {
  generateGenericLabelValueSettingsContentConfig,
  genericLabelValueFieldParseIntoSettingsFormModel,
  genericLabelValueFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { FormBuilderService } from '../../../services/form-builder.service';
import { parseRequiredValidatorParseFieldIntoModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import { setRequiredValidatorUpdateFieldFromModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import {
  getRequiredValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { getConditionalValidationsFormConfig } from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-generator.util';
import { EAvailableConditionalValidations } from '../../../models/form-fields-formly-conditional-validation.model';
import { parseConditionalValidatorsModelFromFieldIntoModel } from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-parse-to-model.util';
import { setConditionalExpressionFromModel } from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-set-from-model.util';
import {
  getConditionalSettingsForm,
  updateConditionalSettingFromFormModel,
} from '../conditional-settings-configurations';

export const defaultRadioConfigFormModel: IGenericLabelValueFieldsConfigFormModel =
  {
    ...fieldsBaseConfigurationFormModel(),
    contentSection: { optionLabelValuePairs: [{ label: '', value: '' }] },
    disabled: false,
    conditionalSettings: {
      settings: [],
    },
  };

export const radioFieldGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: {
        ...defaultRadioConfigFormModel,
        defaultValue: null,
        conditionalSettings: { settings: [] },
      },
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        generateGenericLabelValueSettingsContentConfig('Radio Options'),
        {
          key: 'defaultValue',
          type: EIremboFormlyFieldTypes.customdropdown,
          className: 'mt-2 mb-5',
          props: {
            label: 'Default Value',
            disabled: false,
            required: false,
            placeholder: 'Enter default Value',
            bindLabel: 'label',
            bindValue: 'value',
            multiple: false,
            options: [],
          },
          id: 'defaultValue',
          expressions: {
            'props.options':
              'model?.contentSection?.optionLabelValuePairs ? model?.contentSection?.optionLabelValuePairs: []',
          },
        },
        getConditionalSettingsForm(formBuilderServiceInstance),
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        getRequiredValidatorFormConfig(),
        getValidationFieldsFormConfig(formBuilderServiceInstance),
        getConditionalValidationsFormConfig(
          [EAvailableConditionalValidations.required],
          formBuilderServiceInstance
        ),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const radioFieldParseIntoSettingsFormModel = (
  radioField: FormlyFieldConfig
): IGenericLabelValueFieldsConfigFormModel => {
  const defaultValueIndex = (radioField.props?.['options'] as any[]).findIndex(
    x => x.value === radioField.defaultValue
  );
  return {
    ...genericLabelValueFieldParseIntoSettingsFormModel(radioField),
    defaultValue: defaultValueIndex !== -1 ? radioField.defaultValue : null,
    conditionalSettings:
      radioField.props && radioField.props['conditionalSettings']
        ? radioField.props['conditionalSettings']
        : null,
  };
};

export const radioFieldUpdateFromSettingsFormModel = (
  model: IGenericLabelValueFieldsConfigFormModel,
  multiCheckboxField: FormlyFieldConfig
): FormlyFieldConfig => {
  const field = genericLabelValueFieldUpdateFromSettingsFormModel(
    model,
    multiCheckboxField
  );
  return radioLabelValueFieldUpdateFromSettingsFormModel(model, field);
};

export const radioFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};
  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  model = parseConditionalValidatorsModelFromFieldIntoModel(model, field);
  return model;
};

export const radioFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  inputField: FormlyFieldConfig
): FormlyFieldConfig => {
  inputField = setRequiredValidatorUpdateFieldFromModel(model, inputField);
  inputField = setConditionalExpressionFromModel(model, inputField);
  return inputField;
};

export const radioFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const radioFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

const radioLabelValueFieldUpdateFromSettingsFormModel = (
  model: IGenericLabelValueFieldsConfigFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  const defaultValueIndex = (field.props?.['options'] as any[]).findIndex(
    x => x.value === model.defaultValue
  );
  field.defaultValue = defaultValueIndex !== -1 ? model.defaultValue : null;

  return updateConditionalSettingFromFormModel(field, model);
};
